import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { favoritesApi, type Favorite } from '@/api/customer/favorites';
import { favoriteKeys } from '@/lib/queryKeys';
import { toast } from "@/hooks/use-toast";
import { useAuthStore } from '@/features/shared/auth/store/useAuthStore';
import { AddFavoriteToast, RemoveFavoriteToast } from '@/features/shared/toast/components/ToastVariants';

// ============================================
// QUERIES (GET)
// ============================================

/**
 * Hook to fetch all favorites for the authenticated user
 * 
 * Features:
 * - Fetches favorites with product details
 * - 2 minute stale time for optimal caching
 * - Automatic background revalidation
 * - Handles authentication errors gracefully
 * 
 * Requirements: 3.2, 3.3, 7.1, 7.2
 */
export function useFavorites() {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: favoriteKeys.lists(),
    queryFn: async () => {
      const response = await favoritesApi.getFavorites();
      return response.data;
    },
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000, // 2 minutes - data considered "fresh"
    gcTime: 5 * 60 * 1000, // 5 minutes - time in cache after not being used
    retry: (failureCount, error: any) => {
      // Don't retry on 401 (not authenticated)
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Hook to fetch paginated favorites for the authenticated user
 * 
 * Features:
 * - Supports pagination
 * - Returns metadata about pagination state
 * - Automatic caching per page
 * 
 * Requirements: 3.2, 3.3
 */
export function useFavoritesPaginated(page: number = 1, limit: number = 20) {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: favoriteKeys.paginated({ page, limit }),
    queryFn: () => favoritesApi.getFavoritesPaginated(page, limit),
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Hook to check if a specific product is in favorites
 * 
 * Features:
 * - Checks favorite status for a single product
 * - Cached per product ID
 * - Only runs when productId is provided
 * 
 * Requirements: 1.1, 2.1, 2.2
 */
export function useFavoriteStatus(productId: string | undefined) {
  const { data: favorites, isLoading, error } = useFavorites();

  const isFavorite = favorites?.some((f: Favorite) => f.productId === productId) ?? false;

  return {
    data: { isFavorite },
    isLoading,
    error,
    isFavorite // helper for direct access
  };
}

/**
 * Hook to get the total count of favorites
 * 
 * Features:
 * - Returns just the count for badge displays
 * - Longer stale time since count changes less frequently
 * 
 * Requirements: 3.5
 */
export function useFavoritesCount() {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: [...favoriteKeys.all, 'count'],
    queryFn: async () => {
      const response = await favoritesApi.getFavoritesCount();
      return response.count;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes - count can be less frequent
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================
// MUTATIONS (CREATE, DELETE)
// ============================================

/**
 * Hook to add a product to favorites
 * 
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Automatic cache invalidation on success
 * - Toast notifications for user feedback
 * - Rollback on error
 * 
 * Requirements: 1.1, 1.2, 1.3, 6.1, 6.4, 6.5, 7.1
 */
/**
 * Hook to add a product to favorites
 * 
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Manual cache updates to prevent redundant network requests
 * - Toast notifications for user feedback
 * - Rollback on error
 * 
 * Requirements: 1.1, 1.2, 1.3, 6.1, 6.4, 6.5, 7.1
 */
export function useAddFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId }: { productId: string, product?: { name: string, image: string } }) => favoritesApi.addFavorite(productId),

    // Optimistic update
    onMutate: async ({ productId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: favoriteKeys.lists() });
      await queryClient.cancelQueries({ queryKey: [...favoriteKeys.all, 'count'] });

      // Snapshot previous value
      const previousFavorites = queryClient.getQueryData<Favorite[]>(favoriteKeys.lists());

      // Optimistically add to list (mocking the ID temporarily)
      if (previousFavorites) {
        queryClient.setQueryData<Favorite[]>(favoriteKeys.lists(), (old) => {
          if (!old) return [];
          // Prevent duplicates
          if (old.some(f => f.productId === productId)) return old;

          return [...old, {
            id: 'temp-' + Date.now(),
            userId: 'temp-user',
            productId,
            createdAt: new Date().toISOString(),
            product: undefined // Product details might be missing initially
          }];
        });
      }

      return { previousFavorites };
    },

    onSuccess: (newFavorite, { productId, product }) => {
      // 1. Update List with real data
      queryClient.setQueryData<Favorite[]>(favoriteKeys.lists(), (old) => {
        if (!old) return [newFavorite];
        return old.map(f => f.productId === productId ? newFavorite : f);
      });

      // 2. Update Count
      queryClient.setQueryData([...favoriteKeys.all, 'count'], (old: number | undefined) => {
        return (old || 0) + 1;
      });

      if (product) {
        toast({
          title: "Agregado a favoritos",
          description: (
            <AddFavoriteToast
              description={product.name}
              productImage={product.image}
              onClose={() => toast.dismiss()}
            />
          ),
          variant: "default",
        });
      } else {
        toast.success('Agregado a favoritos', {
          description: 'El producto se agregó correctamente a tus favoritos',
        });
      }
    },

    onError: (error: any, _, context) => {
      // Rollback
      if (context?.previousFavorites) {
        queryClient.setQueryData(favoriteKeys.lists(), context.previousFavorites);
      }

      const errorMessage = error?.response?.data?.message || error.message || 'Error al agregar a favoritos';
      toast.error('Error: ' + errorMessage);
    },
  });
}

/**
 * Hook to remove a product from favorites
 * 
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Manual cache updates to prevent redundant network requests
 * - Toast notifications for user feedback
 * - Rollback on error
 * 
 * Requirements: 2.1, 2.2, 2.4, 6.2, 6.4, 6.5, 7.3
 */
export function useRemoveFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId }: { productId: string, product?: { name: string, image: string } }) => favoritesApi.removeFavorite(productId),

    // Optimistic update
    onMutate: async ({ productId }) => {
      await queryClient.cancelQueries({ queryKey: favoriteKeys.lists() });
      await queryClient.cancelQueries({ queryKey: [...favoriteKeys.all, 'count'] });

      const previousFavorites = queryClient.getQueryData<Favorite[]>(favoriteKeys.lists());

      // Optimistically remove from list
      if (previousFavorites) {
        queryClient.setQueryData<Favorite[]>(favoriteKeys.lists(), (old) => {
          if (!old) return [];
          return old.filter((fav) => fav.productId !== productId);
        });
      }

      return { previousFavorites };
    },

    onSuccess: (_, { product }) => {
      // 1. Update Count
      queryClient.setQueryData([...favoriteKeys.all, 'count'], (old: number | undefined) => {
        return Math.max((old || 1) - 1, 0);
      });

      if (product) {
        toast({
          title: "Eliminado de favoritos",
          description: (
            <RemoveFavoriteToast
              description={product.name}
              productImage={product.image}
              onClose={() => toast.dismiss()}
            />
          ),
          variant: "default",
        });
      } else {
        toast.success('Eliminado de favoritos', {
          description: 'El producto se eliminó correctamente de tus favoritos',
        });
      }
    },

    onError: (error: any, _, context) => {
      if (context?.previousFavorites) {
        queryClient.setQueryData(favoriteKeys.lists(), context.previousFavorites);
      }

      const errorMessage = error?.response?.data?.message || error.message || 'Error al eliminar de favoritos';
      toast.error('Error: ' + errorMessage);
    },
  });
}

/**
 * Hook to toggle a product in favorites
 * 
 * Convenience hook that adds or removes based on current favorite status
 * 
 * Features:
 * - Automatically determines whether to add or remove
 * - Combines loading states from both mutations
 * - Unified error handling
 * 
 * Requirements: 1.1, 1.4, 2.1, 6.4, 6.5
 */
export function useFavoriteToggle(productId: string | undefined) {
  const addMutation = useAddFavorite();
  const removeMutation = useRemoveFavorite();
  const { data: statusData, isLoading: isCheckingStatus } = useFavoriteStatus(productId);

  const toggle = async (product?: { name: string, image: string }) => {
    if (!productId) {
      toast.error('Error', {
        description: 'ID de producto no válido',
      });
      return;
    }

    const isFavorite = statusData?.isFavorite ?? false;

    if (isFavorite) {
      await removeMutation.mutateAsync({ productId, product });
    } else {
      await addMutation.mutateAsync({ productId, product });
    }
  };

  return {
    toggle,
    isFavorite: statusData?.isFavorite ?? false,
    isLoading: addMutation.isPending || removeMutation.isPending || isCheckingStatus,
    error: addMutation.error || removeMutation.error,
  };
}
